import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import HasSomeRights from 'nallian-shared-ui/lib/layout/rights/HasSomeRights';
import { GetMyPrincipal } from 'nallian-shared-ui/lib/selectors';
import { Flex, Link, Button } from 'nallian-shared-ui/lib/components';
import { Table, Params, Actions, Menu } from 'nallian-table-builder-ui';
import { INFINITE_SCROLL, PRINCIPALS_RESOURCE, PRINCIPAL_ACTIONS, ACTIONS } from '../../constants';
import NewPrincipal from './NewPrincipal';
import EditPrincipal from './EditPrincipal';
import PrincipalState from './table/PrincipalState';
import PrincipalStateFilter from './table/PrincipalStateFilter';
import apiRoutes from '../../../../app/api/routes';
import { isSystemOrganisation } from '../../../../app/config/systemOrgs';
import PrincipalFilters from './filter/PrincipalFilters';

export const PRINCIPAL_RIGHTS = [
  'PRINCIPALS.MANAGE.ALL',
  'PRINCIPALS.MANAGE.ALL.EXCEPTSYSTEMORGS',
  'PRINCIPALS.MANAGE.ORGANISATION',
];

const PrincipalsOverview = ({
  forbidManageBecauseSystemOrg,
  allowDeleted,
  organisationId,
  urlRoot,
  isOrgAdmin,
}) => {
  const { t } = useTranslation();
  const { action, actionId } = useParams();

  const principal = useSelector(GetMyPrincipal);

  const isSystemOrg = useMemo(() => isSystemOrganisation(organisationId), [organisationId]);
  const iAmSystemOrg = useMemo(() => isSystemOrganisation(principal?.organisation?.id), [
    principal,
  ]);

  const filtered = useMemo(
    () => ({
      defaults: { organisationId },
    }),
    [organisationId]
  );

  const RESOURCE = `${PRINCIPALS_RESOURCE}_${organisationId}`;

  return (
    <>
      {!forbidManageBecauseSystemOrg && (
        <HasSomeRights rights={PRINCIPAL_RIGHTS}>
          <NewPrincipal
            isOrgAdmin={isOrgAdmin}
            urlRoot={urlRoot}
            organisationId={organisationId}
            open={organisationId && action === ACTIONS.NEW}
          />
          <Flex>
            <Link to={`${urlRoot}/users/${ACTIONS.NEW}`}>
              <Button btnprimary icon="add" content={t('addUser')} />
            </Link>
          </Flex>
        </HasSomeRights>
      )}
      <EditPrincipal
        forbidManageBecauseSystemOrg={forbidManageBecauseSystemOrg}
        urlRoot={urlRoot}
        organisationId={organisationId}
        isSystemOrg={isSystemOrg}
        open={organisationId && actionId && action === PRINCIPAL_ACTIONS.EDIT}
      />
      <PrincipalFilters resource={RESOURCE} organisationId={organisationId} />
      <Table
        infiniteScroll={INFINITE_SCROLL}
        resource={RESOURCE}
        url={apiRoutes.principals()}
        noResultsMessage={t('noUsersFound')}
        pageSize={50}
        filtered={filtered}
        sorted
        noUserSettings
        resizable
      >
        <Actions.Link
          width="2.5rem"
          align="center"
          icon="eye"
          url={`${urlRoot}/users/${PRINCIPAL_ACTIONS.DETAILS}/:id`}
        />
        <Params.String name="fullName" width="16rem" minWidth="16rem" grow />
        <Params.String
          name="primaryEmailAddress"
          label="email"
          width="16rem"
          minWidth="16rem"
          grow
        />
        <Params.Custom
          filter={PrincipalStateFilter}
          content={PrincipalState}
          withoutDeleted={!allowDeleted && !iAmSystemOrg}
          label="state"
          name="activeState"
          width="16rem"
          grow
          noSort
        />
        {!forbidManageBecauseSystemOrg && (
          <Menu width="3.5rem" align="center" hasSomeRights={PRINCIPAL_RIGHTS}>
            <Actions.Link
              label="edit"
              icon="edit"
              url={`${urlRoot}/users/${PRINCIPAL_ACTIONS.EDIT}/:id`}
            />
            <Actions.Delete
              url={`${apiRoutes.principals()}/:id`}
              confirmMessage={t('areYouSureRemovePrincipal')}
              onSuccessMessage={t('principalRemoved')}
            />
          </Menu>
        )}
      </Table>
    </>
  );
};

PrincipalsOverview.propTypes = {
  organisationId: PropTypes.string.isRequired,
  urlRoot: PropTypes.string.isRequired,
  forbidManageBecauseSystemOrg: PropTypes.bool,
  isOrgAdmin: PropTypes.bool.isRequired,
  allowDeleted: PropTypes.bool,
};

PrincipalsOverview.defaultProps = { forbidManageBecauseSystemOrg: false, allowDeleted: false };

export default PrincipalsOverview;
